import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Chip,
    IconButton,
    InputAdornment,
    Stack,
    CircularProgress,
    useTheme,
    Button
} from '@mui/material';
import {
    Edit as EditIcon,
    Visibility,
    VisibilityOff,
    Search as SearchIcon,
    OpenInNew
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { fetchAllCards } from '../../api/cardApi';
import { usePage } from '../../context/PageContext';

const formatDateTimeUTC = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC',
    }).format(date) + ' UTC';
};

const CardManagement = () => {
    const { setPageTitle } = usePage();
    const theme = useTheme();
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filteredCards, setFilteredCards] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        setPageTitle('Card Management');
        const getCards = async () => {
            try {
                const data = await fetchAllCards();
                setCards(data);
                setFilteredCards(data);
            } catch (error) {
                console.error('Failed to fetch cards:', error);
            } finally {
                setLoading(false);
            }
        };
        getCards();
    }, [setPageTitle]);

    useEffect(() => {
        const filtered = cards.filter(card =>
            card.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            card.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            card.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCards(filtered);
        setPage(0); // Reset to first page when filtering
    }, [searchTerm, cards]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedCards = filteredCards.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    if (loading) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading cards...</Typography>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3, m: 'auto', flexGrow: 1 }}>
            <Stack spacing={2} sx={{ mb: 3 }}>
                <TextField
                    placeholder="Search cards..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 400 }}
                />
            </Stack>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow sx={{ 
                            backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black'
                        }}>
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>ID</TableCell>
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>Name</TableCell>
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>Email</TableCell>
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>Status</TableCell>
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>Slug</TableCell>
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>Card Created</TableCell>
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedCards.map((card) => (
                            <TableRow key={card.id} hover>
                                <TableCell>{card.id}</TableCell>
                                <TableCell>
                                    <Button
                                        variant="text"
                                        size="small"
                                        component={Link}
                                        to={`/edit-card/${card.slug}`}
                                        startIcon={<EditIcon />}
                                        sx={{
                                            textTransform: 'none',
                                            justifyContent: 'flex-start',
                                            minWidth: 'auto',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: '8px',
                                            '&:hover': {
                                                backgroundColor: 'primary.light',
                                                color: 'primary.contrastText'
                                            }
                                        }}
                                    >
                                        {card.name}
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    {card.user?.email || '-'}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        icon={card.is_published ? <Visibility /> : <VisibilityOff />}
                                        label={card.is_published ? 'Published' : 'Draft'}
                                        color={card.is_published ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {card.slug}
                                </TableCell>
                                <TableCell>
                                    {formatDateTimeUTC(card.created_at)}
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        component={Link}
                                        to={`/edit-card/${card.slug}`}
                                        color="primary"
                                        size="small"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredCards.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
};

export default CardManagement; 